import React, { useEffect, useMemo, useRef, useState } from 'react';
import { generatePlacementInterpretation } from '../../lib/placementInterpretation';
import { formatPlacementLabel } from '../../lib/chartFormat';

const InterpretationBridge = ({ chart, selectedPlacementKey, onSelectPlacement }) => {
  const placements = chart?.planets || [];
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const dropdownRef = useRef(null);
  const badgeRef = useRef(null);
  const selectedPlacement = useMemo(
    () => placements.find((item) => item.key === selectedPlacementKey) || placements[0],
    [placements, selectedPlacementKey],
  );
  if (!selectedPlacement) return null;
  const selectedIndex = Math.max(0, placements.findIndex((item) => item.key === selectedPlacement.key));

  useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    if (!badgeRef.current || !selectedPlacementKey) return;
    badgeRef.current.classList.remove('chart-selected-badge-animate');
    void badgeRef.current.offsetWidth;
    badgeRef.current.classList.add('chart-selected-badge-animate');
    const t = setTimeout(() => badgeRef.current?.classList.remove('chart-selected-badge-animate'), 600);
    return () => clearTimeout(t);
  }, [selectedPlacementKey]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocumentClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [menuOpen]);

  const block = generatePlacementInterpretation({ placement: selectedPlacement });
  if (!block) return null;

  return (
    <section className="card">
      <h2>How to start reading your chart</h2>
      <div className="chart-summary-grid">
        <article>
          <h4>Planets</h4>
          <p>Planets describe functions: what part of you is acting.</p>
        </article>
        <article>
          <h4>Signs</h4>
          <p>Signs describe style: how the function tends to express.</p>
        </article>
        <article>
          <h4>Houses</h4>
          <p>Houses describe life areas: where this expression becomes visible.</p>
        </article>
        <article>
          <h4>Aspects</h4>
          <p>Aspects describe relationships between functions, including flow and tension.</p>
        </article>
      </div>

      <p className="chart-help">
        Teaching model: <strong>planet = function</strong>, <strong>sign = style</strong>, <strong>house = life arena</strong>.
      </p>

      <div className="chart-sentence-builder">
        <label htmlFor="sentence-builder-select">Sentence Builder</label>
        <div className="chart-select-wrap" ref={dropdownRef}>
          <button
            id="sentence-builder-select"
            type="button"
            className="chart-select-trigger"
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setMenuOpen(true);
                setActiveIndex((prev) => Math.min(prev + 1, placements.length - 1));
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setMenuOpen(true);
                setActiveIndex((prev) => Math.max(prev - 1, 0));
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (menuOpen && placements[activeIndex]) {
                  onSelectPlacement(placements[activeIndex].key);
                }
                setMenuOpen((prev) => !prev);
              }
              if (event.key === 'Escape') {
                setMenuOpen(false);
              }
            }}
          >
            <span className="chart-select-trigger-text">
              <strong>{selectedPlacement.name}</strong> in {selectedPlacement.sign}{selectedPlacement.house ? ` (House ${selectedPlacement.house})` : ''}
            </span>
            <span className="chart-select-caret" aria-hidden="true">▾</span>
          </button>

          {menuOpen && (
            <div className="chart-select-menu" role="listbox" aria-label="Sentence builder placements">
              {placements.map((placement, index) => (
                <button
                  type="button"
                  key={placement.key}
                  role="option"
                  aria-selected={selectedPlacement.key === placement.key}
                  className={`chart-select-option${selectedPlacement.key === placement.key ? ' chart-select-option-selected' : ''}${activeIndex === index ? ' chart-select-option-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    onSelectPlacement(placement.key);
                    setMenuOpen(false);
                  }}
                >
                  <span className="chart-select-option-planet">{placement.name}</span>
                  <span className="chart-select-option-meta"> in {placement.sign}{placement.house ? ` (House ${placement.house})` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="chart-selected-placement-badge" ref={badgeRef} role="status" aria-live="polite">
          <span className="chart-selected-badge-label">Selected placement</span>
          <span className="chart-selected-badge-value">{formatPlacementLabel(selectedPlacement)}</span>
        </div>

        <div className="chart-sentence-card chart-sentence-output">
          <p>
            <strong>{block.sentence}</strong>
          </p>
        </div>

        <div className="chart-sentence-card chart-interpretation-card">
          <h4 className="chart-interpretation-placement-heading">{formatPlacementLabel(selectedPlacement)}</h4>
          <h4>Core pattern</h4>
          <p>{block.corePattern}</p>
          <h4>Possible expressions</h4>
          <ul>
            {block.possibleExpressions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <h4>Observation</h4>
          <p>{block.observation}</p>
        </div>
      </div>
    </section>
  );
};

export default InterpretationBridge;
